import React, { useState, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { colors, spacing, borderRadius, shadows, typography } from '../theme/colors';
import PageLayout from '../components/PageLayout';
import {
  FiChevronDown, FiChevronRight, FiSearch,
  FiSettings, FiDatabase, FiShoppingCart, FiDollarSign,
  FiBox, FiCreditCard, FiBook, FiBarChart2,
  FiCommand, FiCheckSquare, FiPlayCircle
} from 'react-icons/fi';

// ── Keyframes ──
const flowPulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

// ── Workflow Step Indicator Component ──
const WorkflowDiagram = ({ steps, color }) => (
  <WorkflowContainer>
    {steps.map((step, idx) => (
      <React.Fragment key={idx}>
        <WorkflowStep $color={color} $delay={idx * 0.4}>
          <StepNumber $color={color}>{idx + 1}</StepNumber>
          <StepLabel>{step}</StepLabel>
        </WorkflowStep>
        {idx < steps.length - 1 && (
          <WorkflowArrow $color={color} $delay={idx * 0.4 + 0.2}>
            <FiChevronRight />
          </WorkflowArrow>
        )}
      </React.Fragment>
    ))}
  </WorkflowContainer>
);

// ── Section Data ──
const getHelpSections = () => [
  {
    key: 'getting-started',
    icon: FiPlayCircle,
    title: 'Getting Started',
    content: (
      <>
        <HelpText>Welcome to AMAL ERP. Follow these steps to set up your system for the first time.</HelpText>
        <SubSection>
          <SubTitle>1. Company Setup</SubTitle>
          <HelpText>
            Navigate to <strong>Settings &rarr; My Company</strong> to configure your company profile. Enter your
            company name, address, tax registration numbers (GST/VAT), logo, and contact information. This data
            appears on all printed documents such as invoices, purchase orders, and delivery challans.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>2. Financial Year</SubTitle>
          <HelpText>
            Go to <strong>Settings &rarr; Financial Year</strong> to define your accounting period. The financial year
            determines the date range for all reports and trial balances. You can create multiple financial years and
            switch between them. Ensure your opening balances are entered before posting transactions for a new year.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>3. Chart of Accounts</SubTitle>
          <HelpText>
            Set up your <strong>Master Data &rarr; Chart of Accounts</strong> before recording any transactions.
            The chart is organized into five root groups: Assets, Liabilities, Equity, Revenue, and Expenses.
            Create sub-groups and ledger accounts under each. Every financial transaction in the system posts to
            at least two accounts (double-entry bookkeeping).
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>4. Document Settings</SubTitle>
          <HelpText>
            Configure auto-numbering prefixes and sequences for all document types under <strong>Settings &rarr; Document Settings</strong>.
            This ensures consistent numbering for Purchase Orders, Invoices, Receipts, and other documents.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>5. Users &amp; Roles</SubTitle>
          <HelpText>
            Add team members and assign roles under <strong>Settings &rarr; Users &amp; Roles</strong>. Roles control
            which modules and actions each user can access. Share your company join code so users can request access.
          </HelpText>
        </SubSection>
      </>
    )
  },
  {
    key: 'master-data',
    icon: FiDatabase,
    title: 'Master Data',
    content: (
      <>
        <HelpText>Master data forms the foundation of your ERP. Set these up before starting daily transactions.</HelpText>
        <SubSection>
          <SubTitle>Customers</SubTitle>
          <HelpText>
            Manage your customer records under <strong>Master Data &rarr; Customers</strong>. Each customer has a name,
            contact details, billing/shipping addresses, tax registration, credit limit, and payment terms. Customer
            balances are automatically tracked through sale invoices and receipts.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Vendors</SubTitle>
          <HelpText>
            Vendor records are maintained under <strong>Master Data &rarr; Vendors</strong>. Include supplier details,
            bank information for payments, tax IDs, and default payment terms. Vendor balances update automatically
            through purchase invoices and payments.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Items (Chart of Inventory)</SubTitle>
          <HelpText>
            Define your products and materials in <strong>Master Data &rarr; Chart of Inventory</strong>. Each item
            includes a code, description, unit of measure, category, tax rate, and opening stock. Items can be
            organized into groups and sub-groups. Track items by batch number or serial number if required.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Employees</SubTitle>
          <HelpText>
            Add employee records under <strong>Master Data &rarr; Employees</strong>. Employee data is used for
            salesperson assignment on sale orders and performance tracking.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Jobs / Projects</SubTitle>
          <HelpText>
            Create job or project records under <strong>Master Data &rarr; Jobs / Projects</strong> to track costs
            and revenue against specific projects. Link transactions to jobs for project-wise profitability analysis.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Inventory Locations &amp; Banks</SubTitle>
          <HelpText>
            Set up warehouse locations under <strong>Inventory Locations</strong> and bank accounts under <strong>Banks</strong>.
            Stock is tracked per location, and bank accounts are used for bank receipts and bank payments.
          </HelpText>
        </SubSection>
      </>
    )
  },
  {
    key: 'purchase-cycle',
    icon: FiShoppingCart,
    title: 'Purchase Cycle',
    content: (
      <>
        <HelpText>
          The purchase cycle follows a structured workflow from requisition to payment. Each step creates a document
          that links to the next, providing full traceability.
        </HelpText>
        <WorkflowDiagram
          steps={['Purchase Requisition', 'Purchase Order', 'GRN', 'Purchase Invoice', 'Payment']}
          color={colors.warning.dark}
        />
        <SubSection>
          <SubTitle>Purchase Requisition (PR)</SubTitle>
          <HelpText>
            Departments raise purchase requisitions under <strong>Store &rarr; Purchase Requisition</strong> to request
            materials. Approved PRs can be converted into Purchase Orders.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Purchase Order (PO)</SubTitle>
          <HelpText>
            Create purchase orders under <strong>Purchases &rarr; Purchase Order</strong>. Select the vendor,
            add line items with quantities and rates, specify delivery date and payment terms. POs can be created
            from approved PRs or manually. Track PO status through the PO Tracking report.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Goods Receipt Note (GRN)</SubTitle>
          <HelpText>
            When goods arrive, record them under <strong>Purchases &rarr; Goods Receipt (GRN)</strong>. GRNs
            reference a PO and record actual quantities received. Stock is updated automatically upon posting.
            Partial receipts are supported; the PO tracks remaining quantities.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Purchase Invoice (PI)</SubTitle>
          <HelpText>
            Record the vendor bill under <strong>Purchases &rarr; Purchase Invoice</strong>. PIs can reference a
            GRN or be created independently. Posting a PI updates the vendor balance and creates accounting entries
            (Debit: Purchase/Expense account, Credit: Vendor/AP account).
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Payment</SubTitle>
          <HelpText>
            Make payments via <strong>Payments &rarr; Cash Payment</strong> or <strong>Bank Payment</strong>.
            Select the vendor, choose outstanding invoices to clear, and record the payment amount.
            Partial payments are supported.
          </HelpText>
        </SubSection>
      </>
    )
  },
  {
    key: 'sales-cycle',
    icon: FiDollarSign,
    title: 'Sales Cycle',
    content: (
      <>
        <HelpText>
          The sales cycle manages the complete order-to-cash process. Each document links to the next
          for end-to-end visibility.
        </HelpText>
        <WorkflowDiagram
          steps={['Quotation', 'Sale Order', 'Delivery Challan', 'Sale Invoice', 'Receipt']}
          color={colors.primary.main}
        />
        <SubSection>
          <SubTitle>Quotation</SubTitle>
          <HelpText>
            Create customer quotations under <strong>Sales &rarr; Quotation</strong>. Add items, quantities, and
            pricing. Approved quotations can be converted to Sale Orders. Track quotation-to-order conversion rates.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Sale Order (SO)</SubTitle>
          <HelpText>
            Confirmed orders are recorded under <strong>Sales &rarr; Sale Order</strong>. SOs reserve expected
            revenue and can be tracked through the SO Tracking report. Partial fulfillment is supported.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Delivery Challan (DC)</SubTitle>
          <HelpText>
            Dispatch goods against sale orders using <strong>Sales &rarr; Delivery Challan</strong>. Posting a DC
            reduces stock from the specified warehouse location. Multiple DCs can be created against a single SO.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Sale Invoice (SI)</SubTitle>
          <HelpText>
            Generate sale invoices under <strong>Sales &rarr; Sale Invoice</strong>. SIs create accounting entries
            (Debit: Customer/AR account, Credit: Sales Revenue account) and update customer balances. Tax is
            calculated automatically based on item tax settings.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Receipt</SubTitle>
          <HelpText>
            Record customer payments via <strong>Receipts &rarr; Cash Receipt</strong> or <strong>Bank Receipt</strong>.
            Select outstanding invoices to clear and enter the received amount. Partial receipts and advance
            receipts are supported.
          </HelpText>
        </SubSection>
      </>
    )
  },
  {
    key: 'inventory',
    icon: FiBox,
    title: 'Inventory Management',
    content: (
      <>
        <HelpText>
          The inventory module provides full control over stock levels, movements, and manufacturing processes.
        </HelpText>
        <SubSection>
          <SubTitle>Add Stock</SubTitle>
          <HelpText>
            Use <strong>Store &rarr; Add Stock</strong> to manually increase stock for an item at a specific location.
            This is useful for opening stock, stock corrections, or receiving items outside the purchase cycle.
            Each entry creates an inventory journal posting.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Reduce Stock</SubTitle>
          <HelpText>
            Use <strong>Store &rarr; Reduce Stock</strong> to manually decrease stock. Common uses include
            damaged goods write-offs, samples, or corrections. Reason codes can be specified for audit trails.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Stock Transfer</SubTitle>
          <HelpText>
            Transfer stock between warehouse locations using <strong>Store &rarr; Stock Transfer</strong>.
            Select the source and destination locations, add items and quantities. Stock is reduced from the
            source and added to the destination simultaneously.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Bill of Materials (BOM)</SubTitle>
          <HelpText>
            Define product recipes or assemblies under <strong>Store &rarr; Bill of Materials</strong>. A BOM
            lists the raw materials and quantities needed to produce a finished item. BOMs are used by
            Production Orders to consume materials and produce finished goods.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Production Order</SubTitle>
          <HelpText>
            Create production orders under <strong>Store &rarr; Production Order</strong> to manufacture
            finished goods. Select a BOM, specify the quantity to produce, and post the order. Raw materials
            are consumed and finished goods are added to stock automatically.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Gate Passes</SubTitle>
          <HelpText>
            Track material movement in and out of your premises using <strong>Inward Gate Pass</strong> and
            <strong> Outward Gate Pass</strong>. Gate passes provide security documentation for goods entering
            or leaving the facility, whether for purchases, sales, returns, or job work.
          </HelpText>
        </SubSection>
      </>
    )
  },
  {
    key: 'payments-receipts',
    icon: FiCreditCard,
    title: 'Payments & Receipts',
    content: (
      <>
        <HelpText>
          Record all money coming in and going out through the Receipts and Payments modules.
        </HelpText>
        <SubSection>
          <SubTitle>Party Mode vs Petty Mode</SubTitle>
          <HelpText>
            <strong>Party Mode:</strong> Used for customer/vendor transactions. Select a party (customer or vendor),
            then choose outstanding invoices to clear. The system automatically matches the payment against
            the selected invoices and updates the party balance.
          </HelpText>
          <HelpText>
            <strong>Petty Mode:</strong> Used for miscellaneous expenses or income that do not involve a customer
            or vendor. Select expense/income ledger accounts directly. Useful for office supplies, utility bills,
            and other petty cash transactions.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Invoice Clearing</SubTitle>
          <HelpText>
            When recording a receipt or payment in party mode, the system displays all outstanding invoices for
            the selected party. Check the invoices being settled and enter the amount applied to each.
            Partial clearing is supported -- any remaining balance stays outstanding and appears in aging reports.
          </HelpText>
        </SubSection>
        <SubSection>
          <SubTitle>Cash vs Bank</SubTitle>
          <HelpText>
            <strong>Cash Receipt / Cash Payment:</strong> Debits or credits the Cash account.
            <br />
            <strong>Bank Receipt / Bank Payment:</strong> Debits or credits the selected Bank account.
            Choose the bank from your configured bank accounts list.
          </HelpText>
        </SubSection>
      </>
    )
  },
  {
    key: 'journal-entries',
    icon: FiBook,
    title: 'Journal Entries',
    content: (
      <>
        <HelpText>
          Journal entries allow you to record transactions that do not fit the standard purchase, sale,
          receipt, or payment workflows.
        </HelpText>
        <SubSection>
          <SubTitle>General Journal</SubTitle>
          <HelpText>
            Access the general journal under <strong>Journal &rarr; General Journal</strong>. Each journal entry
            consists of multiple debit and credit lines that must balance (total debits = total credits).
            Common uses include:
          </HelpText>
          <BulletList>
            <li>Depreciation entries</li>
            <li>Accruals and prepayments</li>
            <li>Inter-account transfers</li>
            <li>Year-end closing entries</li>
            <li>Correction entries</li>
            <li>Salary and payroll postings</li>
          </BulletList>
        </SubSection>
        <SubSection>
          <SubTitle>Opening Balances</SubTitle>
          <HelpText>
            When migrating to AMAL ERP or starting a new financial year, enter opening balances under
            <strong> Master Data &rarr; Opening Balances</strong>. This sets the starting position for all
            ledger accounts, customer balances, vendor balances, and bank accounts. Ensure total debits
            equal total credits across all opening balance entries.
          </HelpText>
        </SubSection>
      </>
    )
  },
  {
    key: 'reports',
    icon: FiBarChart2,
    title: 'Reports',
    content: (
      <>
        <HelpText>
          AMAL ERP provides comprehensive reports across all modules. All reports support date range filtering,
          PDF export, and drill-down capabilities.
        </HelpText>
        <SubSection>
          <SubTitle>Financial Reports</SubTitle>
          <ReportTable>
            <thead>
              <tr><th>Report</th><th>Description</th></tr>
            </thead>
            <tbody>
              <tr><td>Ledger Report</td><td>Detailed transaction history for any ledger account with running balance</td></tr>
              <tr><td>Trial Balance</td><td>Summary of all account balances at a point in time; verifies debit/credit equality</td></tr>
              <tr><td>Profit &amp; Loss</td><td>Revenue minus expenses for a period; shows net profit or loss</td></tr>
              <tr><td>Balance Sheet</td><td>Snapshot of assets, liabilities, and equity at a given date</td></tr>
              <tr><td>Cash Flow Statement</td><td>Cash inflows and outflows categorized by operating, investing, and financing activities</td></tr>
              <tr><td>Tax Reports</td><td>Tax collected and paid summaries for GST/VAT filing</td></tr>
            </tbody>
          </ReportTable>
        </SubSection>
        <SubSection>
          <SubTitle>Purchase Reports</SubTitle>
          <ReportTable>
            <thead>
              <tr><th>Report</th><th>Description</th></tr>
            </thead>
            <tbody>
              <tr><td>Purchase Summary</td><td>Overview of total purchases by period</td></tr>
              <tr><td>Purchase Register</td><td>Detailed list of all purchase invoices with line items</td></tr>
              <tr><td>Party Purchase Summary</td><td>Purchase totals grouped by vendor</td></tr>
              <tr><td>Vendor Payments</td><td>Payment history and outstanding amounts per vendor</td></tr>
              <tr><td>AP Aging</td><td>Accounts payable grouped by aging buckets (current, 30, 60, 90+ days)</td></tr>
            </tbody>
          </ReportTable>
        </SubSection>
        <SubSection>
          <SubTitle>Sale Reports</SubTitle>
          <ReportTable>
            <thead>
              <tr><th>Report</th><th>Description</th></tr>
            </thead>
            <tbody>
              <tr><td>Sale Summary</td><td>Overview of total sales by period</td></tr>
              <tr><td>Sale Register</td><td>Detailed list of all sale invoices with line items</td></tr>
              <tr><td>Party Sale Summary</td><td>Sales totals grouped by customer</td></tr>
              <tr><td>Collection Report</td><td>Customer payment collection details and trends</td></tr>
              <tr><td>AR Aging</td><td>Accounts receivable grouped by aging buckets</td></tr>
              <tr><td>Salesperson Performance</td><td>Sales metrics and targets per salesperson/employee</td></tr>
            </tbody>
          </ReportTable>
        </SubSection>
        <SubSection>
          <SubTitle>Inventory Reports</SubTitle>
          <ReportTable>
            <thead>
              <tr><th>Report</th><th>Description</th></tr>
            </thead>
            <tbody>
              <tr><td>Stock Summary</td><td>Current stock levels for all items across all locations</td></tr>
              <tr><td>Stock Details</td><td>Detailed stock breakdown by location, batch, and serial number</td></tr>
              <tr><td>Stock Valuation</td><td>Total inventory value using configured valuation method (FIFO/Avg)</td></tr>
              <tr><td>Stock Movement</td><td>Inward and outward movements for each item over a period</td></tr>
              <tr><td>Item History</td><td>Complete transaction history for a specific item</td></tr>
              <tr><td>Low Stock Alert</td><td>Items that have fallen below their minimum reorder level</td></tr>
              <tr><td>Batch / Serial Tracking</td><td>Track items by batch number or serial number across all transactions</td></tr>
            </tbody>
          </ReportTable>
        </SubSection>
      </>
    )
  },
  {
    key: 'keyboard-shortcuts',
    icon: FiCommand,
    title: 'Keyboard Shortcuts',
    content: (
      <>
        <HelpText>
          Use keyboard shortcuts to speed up common actions throughout the application.
        </HelpText>
        <ShortcutTable>
          <thead>
            <tr><th>Shortcut</th><th>Action</th><th>Context</th></tr>
          </thead>
          <tbody>
            <tr><td><Kbd>Alt</Kbd> + <Kbd>N</Kbd></td><td>Create New Document</td><td>Any list/register page</td></tr>
            <tr><td><Kbd>Ctrl</Kbd> + <Kbd>S</Kbd></td><td>Save &amp; Post</td><td>Any form/entry page</td></tr>
            <tr><td><Kbd>Esc</Kbd></td><td>Cancel / Close Modal</td><td>Forms, modals, dropdowns</td></tr>
            <tr><td><Kbd>Ctrl</Kbd> + <Kbd>P</Kbd></td><td>Print / Export PDF</td><td>Reports and documents</td></tr>
            <tr><td><Kbd>Alt</Kbd> + <Kbd>D</Kbd></td><td>Go to Dashboard</td><td>Global</td></tr>
            <tr><td><Kbd>Tab</Kbd></td><td>Move to Next Field</td><td>Forms</td></tr>
            <tr><td><Kbd>Shift</Kbd> + <Kbd>Tab</Kbd></td><td>Move to Previous Field</td><td>Forms</td></tr>
            <tr><td><Kbd>Enter</Kbd></td><td>Confirm Selection</td><td>Dropdowns, search results</td></tr>
            <tr><td><Kbd>Alt</Kbd> + <Kbd>S</Kbd></td><td>Focus Search</td><td>List pages with search</td></tr>
          </tbody>
        </ShortcutTable>
      </>
    )
  },
  {
    key: 'best-practices',
    icon: FiCheckSquare,
    title: 'Best Practices',
    content: (
      <>
        <HelpText>Follow these daily workflow tips to keep your ERP data accurate and up-to-date.</HelpText>
        <SubSection>
          <SubTitle>Daily Workflow</SubTitle>
          <BulletList>
            <li><strong>Morning:</strong> Review the Dashboard for overnight alerts -- low stock items, overdue receivables, and pending approvals.</li>
            <li><strong>Throughout the day:</strong> Record transactions as they occur. Avoid batching entries at end of day to prevent errors and backlogs.</li>
            <li><strong>End of day:</strong> Verify cash balance matches physical cash. Review the Journal Report for the day to catch any posting errors.</li>
          </BulletList>
        </SubSection>
        <SubSection>
          <SubTitle>Data Entry Tips</SubTitle>
          <BulletList>
            <li>Always use the correct document date, not the system date, for backdated transactions.</li>
            <li>Add narration/remarks to every transaction for easy identification later.</li>
            <li>Use the reference field to link related documents (e.g., PO number on a GRN).</li>
            <li>Review the Trial Balance weekly to ensure debits equal credits.</li>
          </BulletList>
        </SubSection>
        <SubSection>
          <SubTitle>Month-End Checklist</SubTitle>
          <BulletList>
            <li>Reconcile all bank accounts against bank statements.</li>
            <li>Review AP Aging and follow up on overdue vendor payments.</li>
            <li>Review AR Aging and follow up on overdue customer collections.</li>
            <li>Run Stock Valuation report and verify physical stock counts.</li>
            <li>Post depreciation and accrual journal entries.</li>
            <li>Generate Profit &amp; Loss and Balance Sheet for management review.</li>
          </BulletList>
        </SubSection>
        <SubSection>
          <SubTitle>Security Tips</SubTitle>
          <BulletList>
            <li>Assign the minimum required role to each user. Avoid giving admin access to everyone.</li>
            <li>Review the user activity log regularly for unauthorized access.</li>
            <li>Change passwords periodically and use strong passwords.</li>
            <li>Back up your data regularly by exporting critical reports.</li>
          </BulletList>
        </SubSection>
      </>
    )
  }
];

// ── Main Help Component ──
const Help = () => {
  const [openSections, setOpenSections] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const toggleSection = (key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Filter sections by search
  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return getHelpSections();
    const term = searchTerm.toLowerCase();
    return getHelpSections().filter(section => {
      // Check title
      if (section.title.toLowerCase().includes(term)) return true;
      // Check content text by rendering to string (approximate)
      const contentString = JSON.stringify(section.content).toLowerCase();
      return contentString.includes(term);
    });
  }, [searchTerm]);

  // Auto-expand filtered sections
  const effectiveOpenSections = useMemo(() => {
    if (searchTerm.trim()) {
      const expanded = {};
      filteredSections.forEach(s => { expanded[s.key] = true; });
      return expanded;
    }
    return openSections;
  }, [searchTerm, filteredSections, openSections]);

  return (
    <PageLayout pageTitle="Help & User Manual" breadcrumb="Reference guide for all ERP modules">
      <HelpContainer>
        {/* Search Bar */}
        <SearchBar>
          <SearchIcon>
            <FiSearch />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search help topics... (e.g. purchase order, trial balance, stock transfer)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <ClearButton onClick={() => setSearchTerm('')}>&times;</ClearButton>
          )}
        </SearchBar>

        {filteredSections.length === 0 && (
          <NoResults>No help topics match your search. Try different keywords.</NoResults>
        )}

        {/* Accordion Sections */}
        {filteredSections.map((section) => {
          const isOpen = effectiveOpenSections[section.key];
          const SectionIcon = section.icon;
          return (
            <AccordionSection key={section.key}>
              <AccordionHeader onClick={() => toggleSection(section.key)} $isOpen={isOpen}>
                <AccordionHeaderLeft>
                  <SectionIconWrap>
                    <SectionIcon />
                  </SectionIconWrap>
                  <AccordionTitle>{section.title}</AccordionTitle>
                </AccordionHeaderLeft>
                <ChevronWrap $isOpen={isOpen}>
                  <FiChevronDown />
                </ChevronWrap>
              </AccordionHeader>
              {isOpen && (
                <AccordionBody>
                  {section.content}
                </AccordionBody>
              )}
            </AccordionSection>
          );
        })}
      </HelpContainer>
    </PageLayout>
  );
};

// ── Styled Components ──

const HelpContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const SearchBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: ${spacing.sm};
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 14px;
  color: ${colors.text.secondary};
  display: flex;
  align-items: center;
  font-size: 18px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 42px;
  font-size: ${typography.fontSize.md};
  font-family: ${typography.fontFamily.primary};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  background: ${colors.background.default};
  color: ${colors.text.primary};
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  font-size: 22px;
  color: ${colors.text.secondary};
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;

  &:hover {
    color: ${colors.text.primary};
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSize.md};
`;

const AccordionSection = styled.div`
  background: ${colors.background.default};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  overflow: hidden;
  box-shadow: ${shadows.sm};
`;

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.md} ${spacing.lg};
  cursor: pointer;
  user-select: none;
  background: ${props => props.$isOpen ? colors.primary.main + '08' : 'transparent'};
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.primary.main}0C;
  }
`;

const AccordionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
`;

const SectionIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${borderRadius.sm};
  background: ${colors.primary.main}12;
  color: ${colors.primary.main};
  font-size: 18px;
  flex-shrink: 0;
`;

const AccordionTitle = styled.h3`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  margin: 0;
`;

const ChevronWrap = styled.div`
  display: flex;
  align-items: center;
  color: ${colors.text.secondary};
  font-size: 20px;
  transition: transform 0.25s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const AccordionBody = styled.div`
  padding: ${spacing.md} ${spacing.lg} ${spacing.lg};
  border-top: 1px solid ${colors.border.light};
`;

const HelpText = styled.p`
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  line-height: 1.7;
  margin: 0 0 ${spacing.sm} 0;

  strong {
    color: ${colors.text.primary};
    font-weight: ${typography.fontWeight.semibold};
  }
`;

const SubSection = styled.div`
  margin-top: ${spacing.md};
`;

const SubTitle = styled.h4`
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.primary.main};
  margin: 0 0 ${spacing.xs} 0;
`;

const BulletList = styled.ul`
  margin: ${spacing.sm} 0 0 ${spacing.md};
  padding-left: ${spacing.md};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  line-height: 1.8;

  li {
    margin-bottom: 4px;
  }

  strong {
    color: ${colors.text.primary};
  }
`;

// ── Workflow Diagram Styles ──

const WorkflowContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  padding: ${spacing.lg} 0;
  justify-content: center;
`;

const WorkflowStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.$color}12;
  border: 2px solid ${props => props.$color}40;
  border-radius: ${borderRadius.md};
  min-width: 110px;
  animation: ${flowPulse} 2.5s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
`;

const StepNumber = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.$color};
  color: ${colors.text.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
`;

const StepLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.primary};
  text-align: center;
  line-height: 1.3;
`;

const WorkflowArrow = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.$color};
  font-size: 20px;
  animation: ${flowPulse} 2.5s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
`;

// ── Report Tables ──

const ReportTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${spacing.sm};
  font-size: ${typography.fontSize.sm};

  th {
    text-align: left;
    padding: ${spacing.sm} ${spacing.md};
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    font-weight: ${typography.fontWeight.semibold};
    border-bottom: 2px solid ${colors.border.light};
  }

  td {
    padding: ${spacing.sm} ${spacing.md};
    color: ${colors.text.secondary};
    border-bottom: 1px solid ${colors.border.light};
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    background: ${colors.neutral[50]};
  }
`;

// ── Keyboard Shortcut Styles ──

const ShortcutTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${spacing.sm};
  font-size: ${typography.fontSize.sm};

  th {
    text-align: left;
    padding: ${spacing.sm} ${spacing.md};
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    font-weight: ${typography.fontWeight.semibold};
    border-bottom: 2px solid ${colors.border.light};
  }

  td {
    padding: ${spacing.sm} ${spacing.md};
    color: ${colors.text.secondary};
    border-bottom: 1px solid ${colors.border.light};
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    background: ${colors.neutral[50]};
  }
`;

const Kbd = styled.kbd`
  display: inline-block;
  padding: 2px 8px;
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.mono};
  color: ${colors.text.primary};
  background: ${colors.neutral[100]};
  border: 1px solid ${colors.border.main};
  border-radius: 4px;
  box-shadow: 0 1px 0 ${colors.border.main};
  line-height: 1.6;
`;

export default Help;
